import { useState, useEffect, useCallback } from "react";
import { Zoom, toast } from "react-toastify";
import {
	globalStyles,
	noop,
	SUPPORTED_COUNTRIES,
} from "@client/shared/constants";
import BulkEditView from "./view";
import Service from "../service";

const NOTIFICATION_TRIGGER_TYPES = {
	PHONE_NUMBER: "phone-number",
	EXTENSION: "extension",
	VOICEMAIL_NOTIFICATION: "voicemail-notification",
};
const defaultCountryIso = "US";
const defaultCountryCallingCode = "+1";

const defaultState = {
	confirming: false,
	currentSection: 0,
	updating: false,
	phoneNumber: defaultCountryCallingCode,
	extensionNumber: "",
	phoneNumberError: null,
	extensionNumberError: null,
	callDirection: "Outbound",
	open: true,
	isParsingPhoneNumber: false,
	isPhoneNumberParsed: false,
	hasVoicemailExtensions: false,
	triggerNotificationType: NOTIFICATION_TRIGGER_TYPES.PHONE_NUMBER,
	selectedCountry: SUPPORTED_COUNTRIES.find(
		(country) => country.isoCode === defaultCountryIso
	),
	voicemail_extensions: null,
};

const BulkEdit = ({
	areAllSelected = false,
	selectedRows = [],
	confirmEdit = true,
	countryList = [],
	onClose = noop,
	onPreferenceChange = noop,
	onSave = noop,
	entries = [],
	loadedExtension = false,
}) => {
	const [state, setState] = useState({
		...defaultState,
	});

	const isSectionComplete = (section) => {
		switch (section) {
			case 0:
				return state.triggerNotificationType ===
					NOTIFICATION_TRIGGER_TYPES.EXTENSION
					? !!state.extensionNumber && !state.extensionNumberError
					: state.triggerNotificationType ===
					  NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
					? !!state.voicemail_extensions
					: !!state.phoneNumber &&
					  !state.phoneNumberError &&
					  state.isPhoneNumberParsed;
			case 1:
				return !!state.callDirection;
			default:
				return false;
		}
	};

	const handleClose = () => {
		if (state.updating) {
			return;
		}
		onClose();
	};

	const parsePhoneNumber = async () => {
		const payload = {
			originalStrings: [state.phoneNumber],
		};
		const { data, error } = await Service.parsePhoneNumber(payload);

		if (error) {
			toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
		}
		const phoneNumbers = data && data.phoneNumbers.map((item) => item.e164);
		setState((prevState) => ({
			...prevState,
			phoneNumber: phoneNumbers && phoneNumbers[0],
			isParsingPhoneNumber: false,
		}));
		onPhoneNumParsedSuccess(true);
	};

	const handleCallDirection = (event) => {
		const callDirection = event.currentTarget.value;
		setState((prevState) => ({
			...prevState,
			callDirection,
		}));
	};
	const handleFormSubmit = async () => {
		toast.info("Updating notifications.", { autoClose: 2000 });
		const timeout = setTimeout(() => {
			toast(toast.INFO, {
				autoClose: 5000,
				transition: Zoom,
			});
		}, 6000);
		setState((prevState) => ({
			...prevState,
			updating: true,
		}));
		const { data, error } = await Service.bulkEdit(
			areAllSelected ? "ALL" : selectedRows.map((row) => typeof row === 'object' ? row.id : row).join(","),
			{
				external_phone_number:
					state.triggerNotificationType ===
					NOTIFICATION_TRIGGER_TYPES.VOICEMAIL_NOTIFICATION
						? null
						: state.phoneNumber,
				extension_id: state.triggerNotificationType === NOTIFICATION_TRIGGER_TYPES.EXTENSION
					? state.extensionNumber?.id
					: state.voicemail_extensions?.id,
				is_extension_number:
					state.triggerNotificationType ===
					NOTIFICATION_TRIGGER_TYPES.EXTENSION,
				call_direction: state.callDirection,
				voicemail_extension: state.voicemail_extensions?.extensionNumber,
				extension_number: state.extensionNumber?.extensionNumber
			}
		);

		clearTimeout(timeout);
		if (error) {
			toast.error(Array.isArray(error) ? error[0] && error[0].message : error);
			setTimeout(() => {
				setState((prevState) => ({
					...prevState,
					updating: false,
					confirming: false,
					open: true,
				}));
			}, 100);
		} else {
			toast.success("Updated successfully.");
			onSave();
			setState((prevState) => ({
				...prevState,
				updating: false,
			}));
		}
	};

	const triggerNotificationOnChange = (event) => {
		setState((prevState) => ({
			...prevState,
			triggerNotificationType: event.target.value,
			phoneNumber: defaultState.phoneNumber,
			extensionNumber: defaultState.extensionNumber,
		}));
	};

	const phoneNumberOnChange = (phoneNumber, phoneNumberError) => {
		setState((prevState) => ({
			...prevState,
			phoneNumber,
			phoneNumberError,
			isPhoneNumberParsed: false,
		}));
	};
	const phoneNumberOnBlur = (event) => {
		if (state.phoneNumber.length < 3 || !!state.phoneNumberError) {
			return;
		}
		setState((prevState) => ({
			...prevState,
			isParsingPhoneNumber: true,
		}));
		parsePhoneNumber();
	};
	const fetchCountryList = async () => {
		const { data, error } = await Service.getCountryList();
		if (error) {
			toast.error("Something went wrong, please contact system administrator.");
		} else {
			if (data && data.length > 0) {
				data.sort((countryA, countryB) =>
					countryA.name > countryB.name ? 1 : -1
				);

				setState((prevState) => ({
					...prevState,
					countryList: data,
				}));
			}
		}
	};
	const fetchExtension = useCallback(async () => {
		const { data, error } = await Service.getExtensions();
		if (error) {
			toast.error("Something went wrong, please contact system administrator.");
		} else {
			setState((prevState) => ({
				...prevState,
				extensionList: (data || []).filter(item => item?.name && item?.extensionNumber),
			}));
		}
	}, []);

	useEffect(() => {
		fetchCountryList();
		fetchExtension();
	}, []);
	useEffect(() => {
		debugger;
		if (selectedRows.length > 0) {
			const selectedEntries = entries.filter(
				(row) => selectedRows.indexOf(row.id) !== -1
			);

			const voicemailSelections = selectedEntries.filter(row => !!row.voicemail_extension);
			if (voicemailSelections && voicemailSelections.length > 0) {
				setState((prevState) => ({
					...prevState,
					hasVoicemailExtensions: true,
				}));
			}
		}
	}, [areAllSelected, selectedRows, entries]);

	const extensionNumberOnChange = (value) => {
		setState((prevState) => ({
			...prevState,
			extensionNumber: value,
			// extensionNumberError: error,
			// valuesChanged: true,
		}));
	};

	const handleKeyDown = (evt) => {
		if (+evt.keyCode === 8 && state.phoneNumber.length < 2) {
			evt.preventDefault();
		}
	};

	const onClickBack = () => {
		setState((prevState) => ({
			...prevState,
			currentSection: 0,
		}));
	};
	const onClickNext =
		state.currentSection === 1
			? () => {
					if (confirmEdit) {
						setState((prevState) => ({
							...prevState,
							open: false,
							confirming: true,
						}));
					} else {
						handleFormSubmit();
					}
			  }
			: () => {
					setState((prevState) => ({
						...prevState,
						currentSection: 1,
					}));
			  };

	const handlePhoneNumber = (evt, error) => {
		const value = evt.currentTarget.value;
		state.countryList.filter((country) => {
			if (`+${country.callingCode}` == value) {
				setState((prevState) => ({
					...prevState,
					selectedCountry:
						value == "+1" ? defaultState.selectedCountry : country,
				}));
			}
		});
		setState((prevState) => ({
			...prevState,
			phoneNumber: value,
			phoneNumberError: error,
			// valuesChanged: true,
		}));
	};
	const handleVoicemailExtension = (event, value) => {
		setState((prevState) => ({
			...prevState,
			voicemail_extensions: value,
			valuesChanged: true,
		}));
	};
	const handleCountrySelection = (selectedOption) => {
		setState((prevState) => ({
			...prevState,
			selectedCountry: selectedOption,
			phoneNumber: `+${selectedOption.callingCode}`,
		}));
	};

	const handleParsePhoneNumber = () => {
		setState((prevState) => ({
			...prevState,
			isParsingPhoneNumber: true,
		}));
	};

	const onPhoneNumParsedSuccess = (value) => {
		setState((prevState) => ({
			...prevState,
			isPhoneNumberParsed: value,
		}));
	};

	return (
		<>
			<BulkEditView
				open={state.open}
				confirming={state.confirming}
				hasVoicemailExtensions={state.hasVoicemailExtensions}
				updating={state.updating}
				handleClose={handleClose}
				areAllSelected={areAllSelected}
				selectedRows={selectedRows}
				currentSection={state.currentSection}
				selectedCountry={state.selectedCountry}
				isSectionComplete={isSectionComplete}
				triggerNotificationType={state.triggerNotificationType}
				isParsingPhoneNumber={state.isParsingPhoneNumber}
				countryList={countryList}
				phoneNumber={state.phoneNumber}
				phoneNumberError={state.phoneNumberError}
				callDirection={state.callDirection}
				confirmEdit={confirmEdit}
				handleFormSubmit={handleFormSubmit}
				handleCallDirection={handleCallDirection}
				NOTIFICATION_TRIGGER_TYPES={NOTIFICATION_TRIGGER_TYPES}
				triggerNotificationOnChange={triggerNotificationOnChange}
				phoneNumberOnChange={phoneNumberOnChange}
				phoneNumberOnBlur={phoneNumberOnBlur}
				extensionNumber={state.extensionNumber}
				extensionNumberOnChange={extensionNumberOnChange}
				onClickBack={onClickBack}
				onClickNext={onClickNext}
				handlePhoneNumber={handlePhoneNumber}
				onPhoneNumParsedSuccess={onPhoneNumParsedSuccess}
				onPreferenceChange={onPreferenceChange}
				handleCountrySelection={handleCountrySelection}
				handleParsePhoneNumber={handleParsePhoneNumber}
				parsePhoneNumber={parsePhoneNumber}
				handleKeyDown={handleKeyDown}
				extensionList={state.extensionList}
				handleVoicemailExtension={handleVoicemailExtension}
				voicemail_extensions={state.voicemail_extensions}
				loadedExtension={loadedExtension}
			/>
		</>
	);
};

export default BulkEdit;
